/**
 * Social-proof interstitial for Variant B — shown right after
 * "Why do you want to lose weight?" (question_type = "weight_loss_reason").
 * Figma Weight-Loss 5313:117527 — headline + 3 stat cards over photos + disclaimer.
 */

import React from "react";
import { clientConfig } from "../../../config";
import * as translator from "../../../utils/translator";
import { Funnel, useFunnel } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

// HL-12357: WL3 loads the WebP copies (same 358x120 art, ~6 KB each instead of ~70 KB).
const statPhotos = (funnel: Funnel): string[] =>
  [1, 2, 3].map(
    (n) =>
      `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/social-proof/stat${n}/stat${n}.${funnel === "wl3" ? "webp" : "png"}`
  );

/** HL-12357: warm the cache while the questions before this page are answered. */
export const preloadSocialProofPhotos = (funnel: Funnel): void => {
  statPhotos(funnel).forEach((src) => {
    new Image().src = src;
  });
};

interface Stat {
  percentKey: string;
  textKey: string;
}

const STATS: Stat[] = [
  { percentKey: "social_proof_stat1_percent", textKey: "social_proof_stat1_text" },
  { percentKey: "social_proof_stat2_percent", textKey: "social_proof_stat2_text" },
  { percentKey: "social_proof_stat3_percent", textKey: "social_proof_stat3_text" },
];

interface SocialProofPageProps {
  language?: string;
}

const SocialProofPage: React.FC<SocialProofPageProps> = ({ language }) => {
  const funnel = useFunnel();
  const photos = statPhotos(funnel);
  return (
    <div className="flex flex-col gap-6">
      <h1
        className="font-['Poppins'] font-semibold text-[24px] lg:text-[32px] leading-[30px] lg:leading-[38px] text-[#08080a]"
        dangerouslySetInnerHTML={{ __html: t(language, "social_proof_title") }}
      />

      <div className="flex flex-col gap-3">
        {STATS.map((stat, i) => (
          <div
            key={i}
            className="relative w-full h-[120px] rounded-xl overflow-hidden"
            style={{ boxShadow: "0px 6px 14px rgba(8,8,10,0.1)" }}
          >
            <img src={photos[i]} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.32)" }} />
            <div className="absolute left-4 bottom-3 text-white font-['Poppins'] font-medium">
              <div className="text-[44px] leading-[48px]">{t(language, stat.percentKey)}</div>
              <div
                className="text-[19px] leading-[24px]"
                dangerouslySetInnerHTML={{ __html: t(language, stat.textKey) }}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="text-center font-['Roboto'] text-[14px] text-[#8c94a3]">
        {t(language, "social_proof_disclaimer")}
      </p>
    </div>
  );
};

export default SocialProofPage;
